import axios from "axios";

// Configure axios defaults for Laravel Sanctum authentication
// These settings are REQUIRED for cookie-based Sanctum authentication
axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;

// Optional: Set the base URL for your Laravel API
// axios.defaults.baseURL = 'http://localhost:16161';

/**
 * Refresh CSRF token from Laravel Sanctum
 * This should be called before sensitive operations to ensure the token is valid
 */
export async function refreshCsrfToken() {
    try {
        await axios.get("/sanctum/csrf-cookie");
        return true;
    } catch (error) {
        console.error("Failed to refresh CSRF token:", error);
        return false;
    }
}

/**
 * Make an axios request with automatic CSRF token refresh on 419 errors
 * This wrapper will retry once with a fresh CSRF token if the request fails with 419
 */
export async function axiosWithCsrfRetry(requestFn) {
    try {
        return await requestFn();
    } catch (error) {
        // If we get a 419 (CSRF token mismatch), refresh the token and retry once
        if (error.response?.status === 419) {
            console.log("CSRF token expired, refreshing and retrying...");
            const refreshed = await refreshCsrfToken();
            if (refreshed) {
                // Wait a bit to ensure the browser has processed the new XSRF cookie
                // This is necessary because Set-Cookie headers need time to be applied
                await new Promise(resolve => setTimeout(resolve, 100));
                return await requestFn();
            }
        }
        throw error;
    }
}

// Flag to prevent recursive 401 handling
let handling401 = false;

/**
 * Setup axios response interceptor for handling 401 errors
 * This will be initialized by App.svelte to have access to router and auth store
 */
export function setupAxiosInterceptors(pushFn, dbInstance) {
    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            // If we get a 401 (unauthorized), logout and redirect to login
            // Skip if we're already handling a 401 or if it's the logout endpoint
            const isLogoutRequest = error.config?.url?.includes('/api/auth/logout');

            if (error.response?.status === 401 && !handling401 && !isLogoutRequest) {
                handling401 = true;
                console.log("401 Unauthorized - clearing local data and redirecting to login");

                try {
                    // Clear local database without calling the API
                    await dbInstance.clearAll();
                } catch (clearError) {
                    console.error("Error clearing local data:", clearError);
                }

                // Redirect to login page
                pushFn("/login");

                // Reset flag after a short delay
                setTimeout(() => {
                    handling401 = false;
                }, 1000);
            }

            return Promise.reject(error);
        }
    );
}

export default axios;
